import type React from "react";

import { Search } from "lucide-react";
import { Input } from "@bcc/components/ui/input";
import { UserNav } from "@bcc/components/user-nav";
import { RoasterLogo } from "@bcc/components/roaster-logo";
import { RoasterProfileSelector } from "@bcc/components/roaster-profile-selector";
import {
  setAppHeaderTestId,
  setAppHeaderSearchTestId,
  setAppHeaderSearchResultTestId,
} from "./app-header.utils";

import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface AppHeaderProps {
  testId?: string;
}

export function AppHeader({ testId }: AppHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Incremental search - fetch results as user types
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const timer = setTimeout(async () => {
        try {
          const response = await fetch(
            `/api/search?q=${encodeURIComponent(searchQuery)}`,
          );
          const data = await response.json();
          setSearchResults(data.results || []);
          setShowResults(true);
        } catch (error) {
          console.error("Search failed:", error);
        }
      }, 300); // Debounce 300ms

      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
    }
  };

  const handleResultClick = (result: any) => {
    setShowResults(false);
    setSearchQuery("");
    navigate(result.url);
  };

  return (
    <header
      data-testid={testId ?? setAppHeaderTestId("container")}
      className="sticky top-0 z-50 w-full border-b border-border-subtle bg-surface-1 shadow-sm"
    >
      <div className="flex h-[72px] items-center gap-4 px-6">
        <div className="flex items-center gap-3">
          {/* BCC Logo */}
          <div className="flex items-center gap-2">
            <div className="relative h-16 w-auto">
              <img
                src="/images/bcc-logo.png"
                alt="Beanz Control Center"
                width={160}
                height={64}
                className="h-16 w-auto object-contain"
              />
            </div>
          </div>

          {/* Divider */}
          <div className="h-14 w-px bg-border-subtle" />

          {/* Roaster Logo */}
          <RoasterLogo />
        </div>

        {/* Search Bar */}
        <form
          onSubmit={handleSearch}
          className="relative mx-auto hidden w-full max-w-md md:block"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder={t("components.appHeader.searchPlaceholder")}
              className="h-10 w-full pl-10 pr-4"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery && setShowResults(true)}
              onBlur={() => setTimeout(() => setShowResults(false), 200)}
              testId={setAppHeaderSearchTestId()}
            />
          </div>

          {/* Incremental search results dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full mt-2 w-full rounded-md border border-border bg-card shadow-lg">
              <div className="max-h-96 overflow-y-auto p-2">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    onClick={() => handleResultClick(result)}
                    className="flex w-full items-start gap-3 rounded-md p-3 text-left hover:bg-muted"
                    data-testid={setAppHeaderSearchResultTestId(index)}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{result.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {result.description}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {result.category}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          <RoasterProfileSelector />
          <UserNav />
        </div>
      </div>
    </header>
  );
}
